import React, { Component } from "react";
import { Button, Col, Container, Row, Table, Form } from "react-bootstrap";
import { FormField } from "../../../../../components/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import ValidationUtil from "../../../../../components/form/validator/ValidationUtil";
import NotificationService from "../../../../../services/notification-service/NotificationService";
import { Modal } from "../../../../../components";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import IcdCodeSelector from "../../component/IcdCodeSelector";

export default class CreateDiagnosisGroupModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            diagnosisGroup: props.diagnosisGroup || {
                diagnosisItems: []
            },
            currentItem: {
                code: "",
                description: "",
                level: null,
                type: null,
                provider: null
            },
            editingItemIndex: -1,
            staffOptions: [],
            formValidated: false
        };

        this.formFields = [];
    }
    
    componentDidMount() {
        this.retrieveStaffOptions();
    }
    
    retrieveStaffOptions = () => {
        RemotingService.getRemoteCall('api/staff/list-base', null, (result) => {
            const staffs = result.items;
            staffs.forEach(i => {
                i.key = "" + i.id;
            });
            this.setState({ staffOptions: staffs });
        });
    }

    updateDiagnosisGroup = () => {
        // Check if at least one diagnosis item has been added
        if (!this.state.diagnosisGroup.diagnosisItems || this.state.diagnosisGroup.diagnosisItems.length === 0) {
            ValidationUtil.showNotification();
            return;
        }

        // Save the diagnosis group without additional validation
        // since each item has already been validated when it was added
        if (this.props.onSave) {
            this.props.onSave(this.state.diagnosisGroup);
        }
    };

    addDiagnosisItem = () => {
        // Set formValidated to true to trigger validation for the current item
        this.setState({ formValidated: true }, () => {
            if (!ValidationUtil.checkWithNotification(this.formFields)) {
                // Reset formValidated if validation fails
                this.setState({ formValidated: false });
                return;
            }

            const { diagnosisGroup, currentItem, editingItemIndex } = this.state;
            const diagnosisItems = [...diagnosisGroup.diagnosisItems];

            if (currentItem.level === "PRINCIPAL") {
                const existingPrincipalIndex = diagnosisItems.findIndex(i => i.level === "PRINCIPAL");
                if (existingPrincipalIndex !== -1 && existingPrincipalIndex !== editingItemIndex) {
                    NotificationService.showNotification({ severity: 'error', summary: 'Validation Error', detail: 'Only one Principal diagnosis is allowed.' });
                    this.setState({ formValidated: false });
                    return;
                }
            }

            if (editingItemIndex >= 0) {
                diagnosisItems[editingItemIndex] = { ...currentItem };
            } else {
                diagnosisItems.push({ ...currentItem });
            }

            // First update the state
            // Clear form fields and validation first
            ValidationUtil.clear(this.formFields);

            // Then update state with empty values
            this.setState({
                diagnosisGroup: {
                    ...diagnosisGroup,
                    diagnosisItems
                },
                currentItem: {
                    code: null,
                    description: null,
                    level: null,
                    type: null,
                    provider: null
                },
                editingItemIndex: -1,
                formValidated: false // Reset formValidated after adding/updating item
            });
        });
    };


    editDiagnosisItem = (index) => {
        const item = this.state.diagnosisGroup.diagnosisItems[index];
        this.setState({
            currentItem: { ...item },
            editingItemIndex: index
        });
    };

    removeDiagnosisItem = (index) => {
        const diagnosisItems = [...this.state.diagnosisGroup.diagnosisItems];
        diagnosisItems.splice(index, 1);
        this.setState({
            diagnosisGroup: {
                ...this.state.diagnosisGroup,
                diagnosisItems
            }
        });
    };

    updateCurrentItem = (field, value) => {
        this.setState({
            currentItem: {
                ...this.state.currentItem,
                [field]: value
            }
        });
    };
    
    handleIcdSelected = (icd) => {
        if (icd) {
            this.setState({
                currentItem: {
                    ...this.state.currentItem,
                    code: icd.code,
                    description: icd.description
                }
            });
        } else {
            this.setState({
                currentItem: {
                    ...this.state.currentItem,
                    code: null,
                    description: null
                }
            });
        }
    };

    renderLevelOptions = () => {
        const { currentItem, editingItemIndex, diagnosisGroup } = this.state;
        const existingPrincipalIndex = diagnosisGroup.diagnosisItems.findIndex(i => i.level === "PRINCIPAL");
        const principalTaken = existingPrincipalIndex !== -1 && existingPrincipalIndex !== editingItemIndex;

        const options = [
            { label: "Principal", value: "PRINCIPAL", isDisabled: principalTaken },
            { label: "Secondary", value: "SECONDARY" }
        ];

        const value = currentItem.level ? options.find(option => option.value === currentItem.level) : null;

        return (
            <Select
                options={options}
                value={value}
                onChange={(option) => this.updateCurrentItem("level", option ? option.value : null)}
                placeholder="Select level"
                isClearable
            />
        );
    };

    renderTypeOptions = () => {
        const options = [
            { label: "Final", value: "FINAL" },
            { label: "Differential 1", value: "DIFFERENTIAL1" },
            { label: "Differential 2", value: "DIFFERENTIAL2" },
            { label: "Provisional", value: "PROVISIONAL" }
        ];

        const { currentItem } = this.state;
        const value = currentItem.type ? options.find(option => option.value === currentItem.type) : null;

        return (
            <Select
                options={options}
                value={value}
                onChange={(option) => this.updateCurrentItem("type", option ? option.value : null)}
                placeholder="Select type"
                isClearable
            />
        );
    };

    renderProviderOptions = () => {
        const options = this.state.staffOptions.map(staff => ({
            label: staff.name,
            value: staff.key
        }));

        const { currentItem } = this.state;
        const value = currentItem.provider ? options.find(option => option.value === currentItem.provider) : null;

        return (
            <Select
                options={options}
                value={value}
                onChange={(option) => this.updateCurrentItem("provider", option ? option.value : null)}
                placeholder="Select provider"
                isClearable
            />
        );
    };

    renderDiagnosisItemForm = () => {
        const { currentItem, formValidated } = this.state;

        return (
            <Container>
                <Row>
                    <Col>
                        <Field 
                            label="Code" 
                            body={
                                <FormField ref={formField => this.formFields.push(formField)} required validateOn={formValidated ? currentItem.code : true}>
                                    <IcdCodeSelector 
                                        icd={currentItem.code ? { code: currentItem.code, description: currentItem.description } : null}
                                        onIcdSelected={this.handleIcdSelected}
                                    />
                                </FormField>
                            }
                        />
                    </Col>
                    <Col>
                        <Field 
                            label="Description" 
                            body={
                                <FormField ref={formField => this.formFields.push(formField)} required validateOn={formValidated ? currentItem.description : true}>
                                    <div className="form-control" style={{ 
                                        minHeight: '38px', 
                                        backgroundColor: '#f8f9fa',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        overflow: 'auto',
                                        maxHeight: '100px'
                                    }}>
                                        {currentItem.description || ''}
                                    </div>
                                </FormField>
                            }
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Field 
                            label="Level" 
                            body={
                                <FormField ref={formField => this.formFields.push(formField)} required validateOn={formValidated ? currentItem.level : true}>
                                    {this.renderLevelOptions()}
                                </FormField>
                            }
                        />
                    </Col>
                    <Col>
                        <Field 
                            label="Type" 
                            body={
                                <FormField ref={formField => this.formFields.push(formField)} required validateOn={formValidated ? currentItem.type : true}>
                                    {this.renderTypeOptions()}
                                </FormField>
                            }
                        />
                    </Col>
                </Row>
                <Row>
                    <Col className="d-flex align-items-end justify-content-end">
                        <Button 
                            variant="primary" 
                            onClick={this.addDiagnosisItem}
                            disabled={!currentItem.code || !currentItem.description || !currentItem.level || !currentItem.type}
                            style={{ marginBottom: "8px" }}
                        >
                            {this.state.editingItemIndex >= 0 ? "Update" : "Add"} Diagnosis Item
                        </Button>
                    </Col>
                </Row>
            </Container>
        );
    };

    renderDiagnosisItemsTable = () => {
        const { diagnosisItems } = this.state.diagnosisGroup;

        if (!diagnosisItems || diagnosisItems.length === 0) {
            return (
                <div className="text-center p-3">
                    No diagnosis items added yet.
                </div>
            );
        }

        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Code</th>
                        <th>Description</th>
                        <th>Level</th>
                        <th>Type</th>
                        <th>Provider</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {diagnosisItems.map((item, index) => (
                        <tr key={index}>
                            <td>{item.code}</td>
                            <td style={{ 
                                maxWidth: '250px', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'normal',
                                wordWrap: 'break-word'
                            }}>{item.description}</td>
                            <td>{item.level}</td>
                            <td>{item.type}</td>
                            <td>{this.getProviderName(item.provider)}</td>
                            <td>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={() => this.editDiagnosisItem(index)}
                                    className="mr-2"
                                >
                                    <FontAwesomeIcon icon={["fas", "edit"]} />
                                </Button>
                                <Button 
                                    variant="outline-danger" 
                                    size="sm" 
                                    onClick={() => this.removeDiagnosisItem(index)}
                                >
                                    <FontAwesomeIcon icon={["fas", "trash"]} />
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    getProviderName = (providerId) => {
        if (!providerId) return '';
        const provider = this.state.staffOptions.find(staff => staff.key === providerId);
        return provider ? provider.name : '';
    };

    render() {
        // Reset formFields array before rendering to avoid stale/duplicate references
        this.formFields = [];
        
        return (
            <Modal 
                visible
                id="diagnosis-group-modal"
                header="Diagnosis"
                closeAction={this.props.onCancel}
                maxWidth={'lg'}
            >
                <div className="diagnosis-group-modal-content" style={{ minWidth: '700px' }}>
                    {this.renderDiagnosisItemForm()}
                    
                    <hr />
                    
                    <h5 className="mb-3">Diagnosis Items</h5>
                    {this.renderDiagnosisItemsTable()}
                    
                    <div className="d-flex justify-content-end mt-3">
                        <Button 
                            variant="secondary" 
                            onClick={this.props.onCancel}
                            className="mr-2"
                            style={{ marginRight: "10px" }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={this.updateDiagnosisGroup}
                        >
                            Save Diagnosis
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }
}

class Field extends Component {
    render() {
        const { label, body } = this.props;
        return (
            <div style={{ display: 'grid', marginBottom: 8 }}>
                <div><b>{label}</b></div>
                {body}
            </div>
        );
    }
}